/**
 * The one audio element in a widget, and the one thing that speaks for it.
 *
 * Every previous player owned its own `<audio>`, which is how a set of twenty
 * rows became twenty independent media elements: two could play at once, none
 * of them knew the others existed, and the only way to stop the noise was to
 * find the row that started it. Here the element is a singleton held by the
 * provider, the state is one record naming the ONE track that is current, and a
 * consumer reads that record through its own key — so a row that is not the
 * playing row sees `Idle` without anyone writing that comparison twice.
 *
 * Four decisions are worth stating.
 *
 * First, the clock is driven by `requestAnimationFrame` while playing rather
 * than by `timeupdate`: the media element fires `timeupdate` about four times a
 * second, and a progress fill declared to track the clock 1:1 would advance in
 * visible 250ms steps. The frame loop is armed ONLY while the status is
 * `Playing`, so nothing runs when nothing is playing.
 *
 * Second, the state lives in a store read through `useSyncExternalStore` with a
 * per-consumer selection, not in React state on a context value. A context
 * value that changes every animation frame re-renders every consumer at frame
 * rate — including the nineteen rows whose own derived values did not move.
 * The selection is compared field by field, so a row that is not current
 * re-renders exactly twice per track change and never in between.
 *
 * Third, the current track is exposed as an OBJECT, not just a key. A dock has
 * to draw the art, the title and the artist of a track it was never handed, and
 * a key cannot carry any of that; routing the record through the provider is
 * also what stops a pillar-side copy drifting from the element's own state.
 *
 * Fourth, the live region is fed from `{ status, title, artist }` and never
 * from the clock, and it is debounced by `CRC_PLAYBACK_ANNOUNCE_DEBOUNCE` — a
 * screen reader that reads a new position four times a second is a screen
 * reader nobody can use. Completion is announced: a preview that ran out is an
 * event, and `Ended` exists so it is not reported as silence.
 *
 * The singleton is a PAGE invariant, not a widget one, and it takes two
 * mechanisms because there are two ways to end up with two elements.
 *
 * A provider inside another provider DEFERS: it owns no element, no store and no
 * live region, and its children read the ancestor's. Nesting used to mean two
 * elements sharing nothing, so a detail tier mounted under a compact tier played
 * the same recording twice, over itself, with both surfaces reading `Pause` and
 * three equalizers running — and each surface could only ever stop its own half.
 *
 * Two providers that are SIBLINGS cannot see each other through the tree, so
 * every owner registers its pause with the module below and a play request stops
 * the others first. The loser's own element fires `pause`, so its store settles
 * on `Paused` and its surfaces come back to `Play` without being told. One
 * preview is audible per page, and the row-to-row handoff inside one widget is
 * untouched: there the store is the same store and nothing is registered twice.
 */
"use client";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from "react";
import { CRC_PLAYBACK_ANNOUNCE_DEBOUNCE } from "./cresultcard.motion";
import { CRC_VISUALLY_HIDDEN } from "./cresultcard.tokens";
import { CresultcardPlaybackStatus } from "./cresultcard.types";

/** The element must not fetch twenty previews the reader never asked for. */
const AUDIO_PRELOAD_NONE = "none";
/** Pausing mid-load rejects the play promise; that is a cancellation, not a failure. */
const ABORT_ERROR_NAME = "AbortError";
const EMPTY_ANNOUNCEMENT = "";
const ANNOUNCE_PLAYING_PREFIX = "Playing ";
const ANNOUNCE_FINISHED_PREFIX = "Finished ";
const ANNOUNCE_ARTIST_SEPARATOR = " — ";
const ANNOUNCE_PAUSED = "Paused";
const ANNOUNCE_BUFFERING = "Buffering";
const ANNOUNCE_UNAVAILABLE = "Preview unavailable";

/**
 * A frame's worth of clock. Below it the position has not visibly moved, so
 * re-rendering every consumer for it buys nothing.
 */
const CRC_PLAYBACK_TICK_EPSILON_SECONDS = 0.03;

const FRACTION_MIN = 0;
const FRACTION_MAX = 1;

/** The provider is layout-neutral: it contributes state, not a box. */
const PLAYBACK_ROOT_STYLE: CSSProperties = { display: "contents" };
const HIDDEN_MEDIA_STYLE: CSSProperties = { display: "none" };

/** The announcement is heard, never seen. */
const LIVE_REGION_STYLE: CSSProperties = CRC_VISUALLY_HIDDEN;

function clampFraction(fraction: number): number {
  if (!Number.isFinite(fraction)) {
    return FRACTION_MIN;
  }
  return Math.min(Math.max(fraction, FRACTION_MIN), FRACTION_MAX);
}

function safeFraction(part: number, whole: number): number {
  return whole > 0 ? clampFraction(part / whole) : FRACTION_MIN;
}

/**
 * The one track the element is pointed at. Title, artist and artwork arrive
 * with the request to play, because the provider is what announces the first
 * two and hands all three to a dock.
 */
export interface CresultcardPlaybackTrack {
  readonly trackKey: string;
  readonly src: string;
  readonly title: string;
  readonly artist?: string;
  /** What a dock draws. The provider never fetches it; it only carries it. */
  readonly artworkSrc?: string;
}

/** What a consumer hands over to start playback. */
export type CresultcardPlaybackPlayInput = CresultcardPlaybackTrack;

/** What the one audio element is doing, and to what. */
export interface CresultcardPlaybackState {
  /** `null` until something has been asked to play. */
  readonly track: CresultcardPlaybackTrack | null;
  readonly status: CresultcardPlaybackStatus;
  readonly elapsedSeconds: number;
  readonly durationSeconds: number;
  /** How much of the media has arrived, in seconds from the start. */
  readonly bufferedSeconds: number;
}

const IDLE_STATE: CresultcardPlaybackState = {
  track: null,
  status: CresultcardPlaybackStatus.Idle,
  elapsedSeconds: 0,
  durationSeconds: 0,
  bufferedSeconds: 0,
};

interface CresultcardPlaybackPatch {
  readonly track?: CresultcardPlaybackTrack | null;
  readonly status?: CresultcardPlaybackStatus;
  readonly elapsedSeconds?: number;
  readonly durationSeconds?: number;
  readonly bufferedSeconds?: number;
}

type CresultcardPlaybackListener = () => void;

interface CresultcardPlaybackStore {
  readonly subscribe: (listener: CresultcardPlaybackListener) => () => void;
  readonly snapshot: () => CresultcardPlaybackState;
  readonly update: (patch: CresultcardPlaybackPatch) => void;
}

function samePlaybackState(
  left: CresultcardPlaybackState,
  right: CresultcardPlaybackState,
): boolean {
  return (
    left.track === right.track &&
    left.status === right.status &&
    left.elapsedSeconds === right.elapsedSeconds &&
    left.durationSeconds === right.durationSeconds &&
    left.bufferedSeconds === right.bufferedSeconds
  );
}

function createCresultcardPlaybackStore(): CresultcardPlaybackStore {
  let state = IDLE_STATE;
  const listeners = new Set<CresultcardPlaybackListener>();
  return {
    subscribe: (listener: CresultcardPlaybackListener): (() => void) => {
      listeners.add(listener);
      return (): void => {
        listeners.delete(listener);
      };
    },
    snapshot: (): CresultcardPlaybackState => state,
    update: (patch: CresultcardPlaybackPatch): void => {
      const next: CresultcardPlaybackState = { ...state, ...patch };
      if (samePlaybackState(state, next)) {
        return;
      }
      state = next;
      listeners.forEach((listener): void => {
        listener();
      });
    },
  };
}

/** How far the media has arrived, read from the element's own ranges. */
function bufferedSecondsOf(element: HTMLAudioElement): number {
  const ranges = element.buffered;
  const position = element.currentTime;
  let ahead = 0;
  for (let index = 0; index < ranges.length; index += 1) {
    const start = ranges.start(index);
    const end = ranges.end(index);
    if (start <= position && position <= end) {
      return end;
    }
    ahead = Math.max(ahead, end);
  }
  return ahead;
}

interface CresultcardPlaybackContextValue {
  readonly store: CresultcardPlaybackStore;
  readonly play: (input: CresultcardPlaybackPlayInput) => void;
  readonly pause: () => void;
  readonly seek: (fraction: number) => void;
}

/**
 * With no provider in scope a consumer reads `Idle` and its controls are inert,
 * which is the same shape the rest of the kit's seams take (`cresultcard.radius`
 * and `cresultcard.variants` both resolve to a default rather than throwing). A
 * widget that renders a transport and never mounts the provider is a wiring
 * defect, not a reason to take a whole deliverable down with an exception.
 */
const NO_PROVIDER_STORE: CresultcardPlaybackStore = {
  subscribe: () => () => undefined,
  snapshot: () => IDLE_STATE,
  update: () => undefined,
};

const NO_PROVIDER_CONTEXT: CresultcardPlaybackContextValue = {
  store: NO_PROVIDER_STORE,
  play: (): void => undefined,
  pause: (): void => undefined,
  seek: (): void => undefined,
};

const CresultcardPlaybackContext =
  createContext<CresultcardPlaybackContextValue | null>(null);

/**
 * Every element-owning provider currently mounted, by the one call that silences
 * it. Two widgets on one page are siblings in the tree, so no context can carry
 * this: the page is the scope the invariant is stated at, so the page is where
 * the registry lives.
 */
const PAGE_PREVIEW_OWNERS = new Set<() => void>();

interface AnnouncementInput {
  readonly status: CresultcardPlaybackStatus;
  readonly title: string | null;
  readonly artist: string | null;
}

/** The five announced states. `Idle` alone says nothing: silence is not an event. */
function announcementFor({ status, title, artist }: AnnouncementInput): string {
  if (status === CresultcardPlaybackStatus.Playing) {
    if (title == null) {
      return EMPTY_ANNOUNCEMENT;
    }
    return artist == null
      ? `${ANNOUNCE_PLAYING_PREFIX}${title}`
      : `${ANNOUNCE_PLAYING_PREFIX}${title}${ANNOUNCE_ARTIST_SEPARATOR}${artist}`;
  }
  if (status === CresultcardPlaybackStatus.Ended) {
    return title == null
      ? EMPTY_ANNOUNCEMENT
      : `${ANNOUNCE_FINISHED_PREFIX}${title}`;
  }
  if (status === CresultcardPlaybackStatus.Paused) {
    return ANNOUNCE_PAUSED;
  }
  if (status === CresultcardPlaybackStatus.Buffering) {
    return ANNOUNCE_BUFFERING;
  }
  if (status === CresultcardPlaybackStatus.Failed) {
    return ANNOUNCE_UNAVAILABLE;
  }
  return EMPTY_ANNOUNCEMENT;
}

interface UsePlaybackSelectionInput<TSelected> {
  readonly store: CresultcardPlaybackStore;
  readonly select: (state: CresultcardPlaybackState) => TSelected;
  readonly isEqual: (left: TSelected, right: TSelected) => boolean;
}

/**
 * One consumer's slice of the store, held stable while the slice itself has
 * not changed. This is the whole reason the clock can run at frame rate in a
 * hundred-row set: a row whose selection is field-for-field identical keeps its
 * previous object, and React skips it.
 */
function usePlaybackSelection<TSelected>({
  store,
  select,
  isEqual,
}: UsePlaybackSelectionInput<TSelected>): TSelected {
  const cacheRef = useRef<{
    source: CresultcardPlaybackState;
    value: TSelected;
  } | null>(null);

  const getSnapshot = useCallback((): TSelected => {
    const source = store.snapshot();
    const cache = cacheRef.current;
    if (cache != null && cache.source === source) {
      return cache.value;
    }
    const next = select(source);
    if (cache != null && isEqual(cache.value, next)) {
      cacheRef.current = { source, value: cache.value };
      return cache.value;
    }
    cacheRef.current = { source, value: next };
    return next;
  }, [isEqual, select, store]);

  return useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot);
}

export interface CresultcardPlaybackProviderProps {
  readonly children: ReactNode;
}

/**
 * A nested provider, which owns nothing. The wrapper is kept so the debug path
 * a widget publishes is the same whether it mounts standalone or inside another
 * widget, and `display: contents` means keeping it costs no box.
 */
function CresultcardPlaybackDeferred({
  children,
}: CresultcardPlaybackProviderProps): ReactElement {
  return (
    <div
      data-el="Cresultcard/PlaybackDeferred/Root"
      data-crc-playback-owner={false}
      style={PLAYBACK_ROOT_STYLE}
    >
      {children}
    </div>
  );
}

export function CresultcardPlaybackProvider({
  children,
}: CresultcardPlaybackProviderProps): ReactElement {
  const ancestor = useContext(CresultcardPlaybackContext);

  if (ancestor != null) {
    return (
      <CresultcardPlaybackDeferred>
        {children}
      </CresultcardPlaybackDeferred>
    );
  }
  return (
    <CresultcardPlaybackOwner>
      {children}
    </CresultcardPlaybackOwner>
  );
}

function CresultcardPlaybackOwner({
  children,
}: CresultcardPlaybackProviderProps): ReactElement {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // A state initialiser, not a ref: the store is created exactly once per
  // provider and never replaced, and nothing reads it during a render.
  const [store] = useState<CresultcardPlaybackStore>(
    createCresultcardPlaybackStore,
  );

  const pause = useCallback((): void => {
    audioRef.current?.pause();
  }, []);

  useEffect((): (() => void) => {
    PAGE_PREVIEW_OWNERS.add(pause);
    return (): void => {
      PAGE_PREVIEW_OWNERS.delete(pause);
    };
  }, [pause]);

  const play = useCallback(
    (input: CresultcardPlaybackPlayInput): void => {
      const element = audioRef.current;
      if (element == null) {
        return;
      }
      // Every other widget on the page goes quiet FIRST, so a second preview is
      // never audible over this one for the length of a load.
      PAGE_PREVIEW_OWNERS.forEach((silence): void => {
        if (silence !== pause) {
          silence();
        }
      });
      const current = store.snapshot().track;
      const isSameSource =
        current != null &&
        current.trackKey === input.trackKey &&
        current.src === input.src;
      if (isSameSource) {
        store.update({ status: CresultcardPlaybackStatus.Buffering });
      } else {
        store.update({
          track: input,
          status: CresultcardPlaybackStatus.Buffering,
          elapsedSeconds: 0,
          durationSeconds: 0,
          bufferedSeconds: 0,
        });
        element.src = input.src;
      }
      void element.play().catch((error: unknown): void => {
        if (error instanceof DOMException && error.name === ABORT_ERROR_NAME) {
          return;
        }
        store.update({ status: CresultcardPlaybackStatus.Failed });
      });
    },
    [pause, store],
  );

  const seek = useCallback(
    (fraction: number): void => {
      const element = audioRef.current;
      if (element == null) {
        return;
      }
      const total = element.duration;
      if (!Number.isFinite(total) || total <= 0) {
        return;
      }
      const next = clampFraction(fraction) * total;
      element.currentTime = next;
      store.update({ elapsedSeconds: next });
    },
    [store],
  );

  /**
   * The clock, at frame rate, and only while sound is actually moving. The
   * loop is armed by a store subscription rather than by a render, so the
   * provider itself never re-renders for a tick.
   */
  useEffect((): (() => void) => {
    let frame = 0;
    const tick = (): void => {
      const element = audioRef.current;
      if (element != null) {
        const position = element.currentTime;
        const settled = store.snapshot().elapsedSeconds;
        const moved =
          Math.abs(position - settled) >= CRC_PLAYBACK_TICK_EPSILON_SECONDS;
        store.update({
          elapsedSeconds: moved ? position : settled,
          bufferedSeconds: bufferedSecondsOf(element),
        });
      }
      frame = window.requestAnimationFrame(tick);
    };
    const sync = (): void => {
      const playing =
        store.snapshot().status === CresultcardPlaybackStatus.Playing;
      if (playing && frame === 0) {
        frame = window.requestAnimationFrame(tick);
        return;
      }
      if (!playing && frame !== 0) {
        window.cancelAnimationFrame(frame);
        frame = 0;
      }
    };
    const unsubscribe = store.subscribe(sync);
    sync();
    return (): void => {
      unsubscribe();
      if (frame !== 0) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [store]);

  const contextValue = useMemo<CresultcardPlaybackContextValue>(
    (): CresultcardPlaybackContextValue => ({ store, play, pause, seek }),
    [pause, play, seek, store],
  );

  return (
    <CresultcardPlaybackContext.Provider value={contextValue}>
      <div
        data-el="Cresultcard/PlaybackOwner/Root"
        data-crc-playback-owner
        style={PLAYBACK_ROOT_STYLE}
      >
        {children}
        <CresultcardPlaybackAnnouncer
          store={store}
        />
        {/* The one media element in the widget. Its `src` is assigned
            imperatively in `play`, so a play request never has to wait for a
            React commit to point the element at the track. */}
        <audio
          data-el="Cresultcard/PlaybackOwner/Media"
          onDurationChange={(event): void => {
            const next = event.currentTarget.duration;
            store.update({
              durationSeconds: Number.isFinite(next) ? next : 0,
            });
          }}
          onEnded={(event): void => {
            store.update({
              status: CresultcardPlaybackStatus.Ended,
              elapsedSeconds: 0,
              bufferedSeconds: bufferedSecondsOf(event.currentTarget),
            });
          }}
          onError={(): void => {
            store.update({ status: CresultcardPlaybackStatus.Failed });
          }}
          onPause={(event): void => {
            // `pause` fires for three different things and only one of them is
            // a reader pausing. It fires as playback runs out — an ending. It
            // fires when the element gives up on a source it could not load,
            // and again when the play promise rejects. Reporting either of
            // those as `Paused` does not just mislabel the announcement: it
            // overwrites the `Failed` that `onError` and `play().catch` set,
            // which is what left a dead preview wearing a live transport, a
            // positive tone and no failure text — the entire designed failure
            // presentation unreachable.
            const element = event.currentTarget;
            if (element.ended || element.error != null) {
              return;
            }
            if (
              store.snapshot().status === CresultcardPlaybackStatus.Failed
            ) {
              return;
            }
            store.update({ status: CresultcardPlaybackStatus.Paused });
          }}
          onPlaying={(): void => {
            store.update({ status: CresultcardPlaybackStatus.Playing });
          }}
          onProgress={(event): void => {
            store.update({
              bufferedSeconds: bufferedSecondsOf(event.currentTarget),
            });
          }}
          onStalled={(): void => {
            store.update({ status: CresultcardPlaybackStatus.Buffering });
          }}
          onTimeUpdate={(event): void => {
            store.update({ elapsedSeconds: event.currentTarget.currentTime });
          }}
          onWaiting={(): void => {
            store.update({ status: CresultcardPlaybackStatus.Buffering });
          }}
          preload={AUDIO_PRELOAD_NONE}
          ref={audioRef}
          style={HIDDEN_MEDIA_STYLE}
        />
      </div>
    </CresultcardPlaybackContext.Provider>
  );
}

interface CresultcardAnnouncementSelection {
  readonly status: CresultcardPlaybackStatus;
  readonly title: string | null;
  readonly artist: string | null;
}

function selectAnnouncement(
  state: CresultcardPlaybackState,
): CresultcardAnnouncementSelection {
  return {
    status: state.status,
    title: state.track?.title ?? null,
    artist: state.track?.artist ?? null,
  };
}

function sameAnnouncement(
  left: CresultcardAnnouncementSelection,
  right: CresultcardAnnouncementSelection,
): boolean {
  return (
    left.status === right.status &&
    left.title === right.title &&
    left.artist === right.artist
  );
}

interface CresultcardPlaybackAnnouncerProps {
  readonly store: CresultcardPlaybackStore;
}

/**
 * One announcement per settled state. The selection deliberately excludes the
 * clock: the rule is that playback is announced on a debounce and NEVER per
 * time tick, and the way to guarantee it is for this component not to be able
 * to see the tick at all.
 */
function CresultcardPlaybackAnnouncer({
  store,
}: CresultcardPlaybackAnnouncerProps): ReactElement {
  const selection = usePlaybackSelection<CresultcardAnnouncementSelection>({
    isEqual: sameAnnouncement,
    select: selectAnnouncement,
    store,
  });
  const [announcement, setAnnouncement] = useState<string>(EMPTY_ANNOUNCEMENT);

  useEffect((): (() => void) => {
    const message = announcementFor(selection);
    const timer = window.setTimeout((): void => {
      setAnnouncement(message);
    }, CRC_PLAYBACK_ANNOUNCE_DEBOUNCE);
    return (): void => {
      window.clearTimeout(timer);
    };
  }, [selection]);

  return (
    <div
      aria-atomic
      aria-live="polite"
      data-el="Cresultcard/PlaybackAnnouncer/Status"
      style={LIVE_REGION_STYLE}
    >
      {announcement}
    </div>
  );
}

interface UseCresultcardPlaybackInput {
  /** The key this consumer is asking about — and only this one. */
  readonly trackKey: string;
}

export interface CresultcardPlaybackHandle {
  /** `Idle` whenever the current track is somebody else's. */
  readonly status: CresultcardPlaybackStatus;
  /** 0…1. Zero for a key that is not current, so a track never shows a stale fill. */
  readonly elapsedFraction: number;
  readonly elapsedSeconds: number;
  readonly durationSeconds: number;
  /** 0…1 of the media that has arrived. Zero for a key that is not current. */
  readonly bufferedFraction: number;
  /** Whether the one audio element is pointed at THIS key. */
  readonly isCurrent: boolean;
  readonly play: (input: CresultcardPlaybackPlayInput) => void;
  readonly pause: () => void;
  readonly seek: (fraction: number) => void;
}

interface CresultcardKeyedSelection {
  readonly status: CresultcardPlaybackStatus;
  readonly elapsedSeconds: number;
  readonly durationSeconds: number;
  readonly bufferedSeconds: number;
  readonly isCurrent: boolean;
}

function sameKeyedSelection(
  left: CresultcardKeyedSelection,
  right: CresultcardKeyedSelection,
): boolean {
  return (
    left.status === right.status &&
    left.elapsedSeconds === right.elapsedSeconds &&
    left.durationSeconds === right.durationSeconds &&
    left.bufferedSeconds === right.bufferedSeconds &&
    left.isCurrent === right.isCurrent
  );
}

const IDLE_SELECTION: CresultcardKeyedSelection = {
  status: CresultcardPlaybackStatus.Idle,
  elapsedSeconds: 0,
  durationSeconds: 0,
  bufferedSeconds: 0,
  isCurrent: false,
};

/**
 * One consumer's view of the one element. `pause` and `seek` are no-ops unless
 * this key is the current one: a row cannot pause a track it is not playing,
 * and a scrub gesture that lands on the wrong row is exactly the defect a
 * per-row `<audio>` used to produce.
 */
export function useCresultcardPlayback({
  trackKey,
}: UseCresultcardPlaybackInput): CresultcardPlaybackHandle {
  const context = useContext(CresultcardPlaybackContext) ?? NO_PROVIDER_CONTEXT;
  const { store, play, pause: pauseCurrent, seek: seekCurrent } = context;

  const select = useCallback(
    (state: CresultcardPlaybackState): CresultcardKeyedSelection =>
      state.track?.trackKey === trackKey
        ? {
            status: state.status,
            elapsedSeconds: state.elapsedSeconds,
            durationSeconds: state.durationSeconds,
            bufferedSeconds: state.bufferedSeconds,
            isCurrent: true,
          }
        : IDLE_SELECTION,
    [trackKey],
  );

  const selection = usePlaybackSelection<CresultcardKeyedSelection>({
    isEqual: sameKeyedSelection,
    select,
    store,
  });
  const isCurrent = selection.isCurrent;

  const pause = useCallback((): void => {
    if (!isCurrent) {
      return;
    }
    pauseCurrent();
  }, [isCurrent, pauseCurrent]);

  const seek = useCallback(
    (fraction: number): void => {
      if (!isCurrent) {
        return;
      }
      seekCurrent(fraction);
    },
    [isCurrent, seekCurrent],
  );

  return useMemo<CresultcardPlaybackHandle>(
    (): CresultcardPlaybackHandle => ({
      status: selection.status,
      elapsedFraction: safeFraction(
        selection.elapsedSeconds,
        selection.durationSeconds,
      ),
      elapsedSeconds: selection.elapsedSeconds,
      durationSeconds: selection.durationSeconds,
      bufferedFraction: safeFraction(
        selection.bufferedSeconds,
        selection.durationSeconds,
      ),
      isCurrent,
      play,
      pause,
      seek,
    }),
    [isCurrent, pause, play, seek, selection],
  );
}

export interface CresultcardPlaybackCurrent {
  /** The track the one element is pointed at, as an object a dock can draw. */
  readonly track: CresultcardPlaybackTrack | null;
  readonly status: CresultcardPlaybackStatus;
  readonly elapsedSeconds: number;
  readonly durationSeconds: number;
  readonly elapsedFraction: number;
  readonly bufferedFraction: number;
  readonly play: (input: CresultcardPlaybackPlayInput) => void;
  readonly pause: () => void;
  readonly seek: (fraction: number) => void;
}

function selectCurrent(
  state: CresultcardPlaybackState,
): CresultcardPlaybackState {
  return state;
}

/**
 * The whole current record, for the one consumer that needs it: a dock, which
 * renders the art, title and artist of a track no row handed it.
 */
export function useCresultcardPlaybackCurrent(): CresultcardPlaybackCurrent {
  const context = useContext(CresultcardPlaybackContext) ?? NO_PROVIDER_CONTEXT;
  const { store, play, pause, seek } = context;
  const state = usePlaybackSelection<CresultcardPlaybackState>({
    isEqual: samePlaybackState,
    select: selectCurrent,
    store,
  });

  return useMemo<CresultcardPlaybackCurrent>(
    (): CresultcardPlaybackCurrent => ({
      track: state.track,
      status: state.status,
      elapsedSeconds: state.elapsedSeconds,
      durationSeconds: state.durationSeconds,
      elapsedFraction: safeFraction(
        state.elapsedSeconds,
        state.durationSeconds,
      ),
      bufferedFraction: safeFraction(
        state.bufferedSeconds,
        state.durationSeconds,
      ),
      play,
      pause,
      seek,
    }),
    [pause, play, seek, state],
  );
}
